// components/PdfCard.tsx
import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faFilePdf,
	faDownload,
	faTrashAlt,
	faFileCirclePlus,
	faArrowsRotate,
} from '@fortawesome/free-solid-svg-icons';
import { apiResource } from 'common/services/apiClient';

interface PdfCardProps<
	T extends { id: number; document?: { id: number; path: string } },
> {
	resource: string;
	entity?: T;
	onEntityUpdate?: (updated: T) => void;
	checkCanGenerate?: (entity: T) => boolean;
	loading?: boolean;
	size?: 'md' | 'lg';
	className?: string;
}

export const PdfCard = <
	T extends { id: number; document?: { id: number; path: string } },
>({
	resource,
	entity,
	onEntityUpdate,
	checkCanGenerate,
	loading = false,
	size = 'md',
	className,
}: PdfCardProps<T>) => {
	const [currentEntity, setCurrentEntity] = useState(entity);
	const api = apiResource<T>(resource);

	// Sync avec props si l'entité change
	React.useEffect(() => {
		setCurrentEntity(entity);
	}, [entity]);

	// Skeleton loader
	if (loading) {
		return (
			<div
				className={`
                    mx-auto p-6 rounded-lg shadow-md
                    ${size === 'lg' ? 'bg-white' : 'bg-gray-50'}
                    ${className ?? ''}
                    `}
			>
				<h2
					className={` font-bold mb-4
                    ${size === 'lg' ? 'text-2xl ' : 'text-lg text-gray-800'}
                    `}
				>
					<FontAwesomeIcon icon={faFilePdf} className="w-6 h-6 text-gray-700" />{' '}
					Document
				</h2>
				{/* Skeleton for filename and icon en une ligne */}
				<div className="flex items-center">
					<div className="h-4 bg-gray-200 rounded w-1/3"></div>
					<div className="h-6 w-6 bg-gray-200 rounded-full ml-2"></div>
				</div>
			</div>
		);
	}

	const token = localStorage.getItem('token');
	if (!token) return null;

	const generatePdf = async () => {
		if (!currentEntity?.id) return;

		if (checkCanGenerate && !checkCanGenerate(currentEntity)) return;

		const { documentId, path } = await api.generatePdf(currentEntity.id, token);

		setCurrentEntity((prev) => {
			if (!prev) return prev;

			const updated = { ...prev, document: { id: documentId, path } };
			onEntityUpdate?.(updated); // update parent ici avec la valeur correcte
			return updated;
		});

		/*console.log('Opening generated PDF...');*/
		const blob = await api.downloadPdf(currentEntity.id, token);
		const url = window.URL.createObjectURL(blob);
		window.open(url, '_blank');
	};

	const downloadPdf = async () => {
		if (!currentEntity?.document || !currentEntity.id) return;

		const blob = await api.downloadPdf(currentEntity.id, token);
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		const filename =
			currentEntity.document.path.split(/[/\\]/).pop() ?? 'document.pdf';
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		a.remove();
	};

	const deletePdf = async () => {
		if (!currentEntity?.document || !currentEntity.id) return;

		await api.deletePdf(currentEntity.id, token);

		setCurrentEntity((prev) => {
			if (!prev) return prev;
			const updated = { ...prev, document: undefined };
			onEntityUpdate?.(updated);
			return updated;
		});
	};

	return (
		<div
			className={`
                    mx-auto p-6 rounded-lg shadow-md
                    ${size === 'lg' ? 'bg-white' : 'bg-gray-50'}
                    ${className ?? ''}
                    `}
		>
			<h2
				className={` font-bold mb-4
                    ${size === 'lg' ? 'text-2xl ' : 'text-lg text-gray-800'}
                    `}
			>
				<FontAwesomeIcon icon={faFilePdf} className="w-6 h-6 text-gray-700" />{' '}
				Document
			</h2>
			{currentEntity?.document?.path ? (
				<>
					{currentEntity.document.path.split(/[/\\]/).pop()}
					<button
						type="button"
						className="ml-2 text-gray-400 hover:text-blue-800"
						onClick={downloadPdf}
					>
						<FontAwesomeIcon icon={faDownload} />
					</button>
					<button
						type="button"
						className="ml-2 text-gray-400 hover:text-blue-800"
						onClick={generatePdf}
					>
						<FontAwesomeIcon icon={faArrowsRotate} />
					</button>
					<button
						type="button"
						className="ml-2 text-gray-400 hover:text-red-800"
						onClick={deletePdf}
					>
						<FontAwesomeIcon icon={faTrashAlt} />
					</button>
				</>
			) : (
				<>
					<span className="text-gray-500">Aucun document généré</span>
					<button
						type="button"
						className="ml-2 text-gray-400 hover:text-green-800"
						onClick={generatePdf}
					>
						<FontAwesomeIcon icon={faFileCirclePlus} />
					</button>
				</>
			)}
		</div>
	);
};
export default PdfCard;
